import { useCallback } from "react";
import { useSnack } from "./useSnack";
import { useUploadFileMutation } from "generated/graphql";
import { convertHeicToJpeg } from "utils/heic";

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data:...;base64, prefix
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const useS3 = () => {
  const showSnack = useSnack();
  const [, uploadFileMutation] = useUploadFileMutation();

  const uploadToS3 = useCallback(
    async (originalFile: File, folderPath: string): Promise<string | null> => {
      try {
        const file = await convertHeicToJpeg(originalFile);

        const base64Data = await fileToBase64(file);

        const response = await uploadFileMutation({
          options: {
            base64Data,
            fileName: file.name,
            contentType: file.type,
            folderPath,
          },
        });

        if (response.error) {
          throw new Error(response.error.message || "Upload request failed");
        }

        if (response.data?.uploadFile.error) {
          throw new Error(response.data.uploadFile.error);
        }

        const fileUrl = response.data?.uploadFile.url;
        if (!fileUrl) {
          throw new Error("No URL returned from upload");
        }

        showSnack(`${file.name} uploaded successfully!`, "success");
        return fileUrl;
      } catch (error) {
        console.error("Upload error:", error);
        showSnack(
          error instanceof Error ? error.message : "Failed to upload file",
          "error",
        );
        return null;
      }
    },
    [showSnack, uploadFileMutation],
  );

  return {
    uploadToS3,
  };
};
